import type { DraggableEventHandler } from "react-draggable";

import { ActionIcon, Popover, ScrollArea, Stack, Tabs, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRef } from "react";
import Draggable from "react-draggable";

import { ClaimTab } from "./ClaimTab";
import { GaiaTab } from "./GaiaTab";
import { IVRTab } from "./IVRTab";
import { LongFormAiTab } from "./LongFormAiTab";
import { MockTranscriptEventsTab } from "./MockTranscriptEventsTab";

type EventEmitterProps = {
    partner: string;
    sessionId: string;
};

export function EventEmitter({ partner, sessionId }: EventEmitterProps) {
    const [opened, { toggle, close }] = useDisclosure(false);
    const nodeRef = useRef<HTMLDivElement>(null);
    const didDrag = useRef(false);

    const onDragStart: DraggableEventHandler = () => {
        didDrag.current = false;
    };
    const onDrag: DraggableEventHandler = () => {
        didDrag.current = true;
    };
    const handleToggle = () => {
        if (!didDrag.current) toggle();
    };

    return (
        <Draggable
            nodeRef={nodeRef}
            bounds="body"
            cancel=".mantine-Popover-dropdown"
            defaultPosition={{ x: 16, y: window.innerHeight - 106 }}
            onStart={onDragStart}
            onDrag={onDrag}
        >
            <div ref={nodeRef} style={{ position: "fixed", zIndex: 9999, cursor: "grab" }}>
                <Popover width={380} position="top-end" shadow="md" opened={opened} onClose={close} zIndex={9999}>
                    <Popover.Target>
                        <Tooltip label="Event Emitter (Dev Tool) - Drag to move">
                            <ActionIcon variant="light" color="grape" size="lg" onClick={handleToggle}>
                                🔌
                            </ActionIcon>
                        </Tooltip>
                    </Popover.Target>

                    <Popover.Dropdown>
                        <ScrollArea.Autosize mah={500}>
                            <Stack gap="sm" pr="xs">
                                <Text fw={600} size="sm">
                                    Event Emitter
                                </Text>
                                <Text size="xs" c="dimmed">
                                    Session: {sessionId || "No session"}
                                </Text>

                                <Tabs defaultValue="gaia">
                                    <Tabs.List>
                                        <Tabs.Tab value="gaia">GAIA</Tabs.Tab>
                                        <Tabs.Tab value="long-form-ai">Long Form AI</Tabs.Tab>
                                        <Tabs.Tab value="ivr">IVR</Tabs.Tab>
                                        <Tabs.Tab value="claim">Claim</Tabs.Tab>
                                        <Tabs.Tab value="mock-transcript">Mock Transcript</Tabs.Tab>
                                    </Tabs.List>

                                    <Tabs.Panel value="gaia" pt="sm">
                                        <GaiaTab partner={partner} sessionId={sessionId} />
                                    </Tabs.Panel>

                                    <Tabs.Panel value="long-form-ai" pt="sm">
                                        <LongFormAiTab sessionId={sessionId} />
                                    </Tabs.Panel>

                                    <Tabs.Panel value="ivr" pt="sm">
                                        <IVRTab />
                                    </Tabs.Panel>

                                    <Tabs.Panel value="claim" pt="sm">
                                        <ClaimTab sessionId={sessionId} />
                                    </Tabs.Panel>

                                    <Tabs.Panel value="mock-transcript" pt="sm">
                                        <MockTranscriptEventsTab partner={partner} sessionId={sessionId} />
                                    </Tabs.Panel>
                                </Tabs>
                            </Stack>
                        </ScrollArea.Autosize>
                    </Popover.Dropdown>
                </Popover>
            </div>
        </Draggable>
    );
}
